import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/core/auth/AuthContext';
import { laborRatesService } from '../services/laborRates.service';
import type { LaborRateCreatePayload, LaborRateUpdatePayload } from '../types/laborRates.types';
import { toast } from 'sonner';

export function useLaborRates() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const userId = profile?.id || '';
  const queryClient = useQueryClient();

  const queryKey = ['inventory', 'labor-rates', companyId];

  const query = useQuery({
    queryKey,
    queryFn: () => laborRatesService.fetchRates(companyId),
    enabled: !!companyId,
  });

  const createMutation = useMutation({
    mutationFn: (payload: LaborRateCreatePayload) => laborRatesService.createRate(userId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Thêm đơn giá thành công');
    },
    onError: (err: any) => {
      console.error(err);
      toast.error(err.message || 'Lỗi khi thêm đơn giá');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: LaborRateUpdatePayload }) => 
      laborRatesService.updateRate(userId, companyId, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Cập nhật đơn giá thành công');
    },
    onError: (err: any) => {
      console.error(err);
      toast.error(err.message || 'Lỗi khi cập nhật đơn giá');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => laborRatesService.deleteRate(userId, companyId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Xóa đơn giá thành công');
    },
    onError: (err: any) => {
      console.error(err);
      toast.error(err.message || 'Lỗi khi xóa đơn giá');
    },
  });

  return {
    data: query.data || [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    createRate: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateRate: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteRate: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}
